import * as React from 'react';
import classNames from 'classnames';
import { MediaSet } from '@/types/media';
import { Icon } from '../Icon';
import { Image } from '../Image';
import { Link } from '../Link';
import styles from './AuthorCard.module.scss';

export interface AuthorCardProps {
	readonly className?: string;
	readonly image?: MediaSet;
	readonly name: string;
	readonly jobTitle?: string;
	readonly biography?: string;
	readonly linkedInUrl?: string;
	readonly emailAddress?: string;
	readonly linkedInLabel: string;
	readonly contactLabel: string;
	/** Spoken link text, naming the author, since "LinkedIn" alone has no context in a list of links */
	readonly linkedInAriaLabel?: string;
	readonly contactAriaLabel?: string;
	readonly lazyLoading?: boolean;
}

/**
 * Generic AuthorCard component, used for showing an author profile, e.g. the author bio at the bottom of an article.
 */
export const AuthorCard: React.FunctionComponent<AuthorCardProps> = ({
	className,
	image,
	name,
	jobTitle,
	biography,
	linkedInUrl,
	emailAddress,
	linkedInLabel,
	contactLabel,
	linkedInAriaLabel,
	contactAriaLabel,
	lazyLoading,
}) => {
	const nameId = React.useId();

	// Without an author name there is nothing meaningful to show
	if (!name) {
		return null;
	}

	const hasSocialLinks = Boolean(linkedInUrl || emailAddress);

	return (
		// Naming the region makes it a findable landmark, and tells the user which author it covers
		<aside className={classNames(styles.wrapper, className)} aria-labelledby={nameId}>
			<div className={styles.headerRow}>
				<div className={styles.imageWrapper}>
					<Image
						className={styles.image}
						{...image}
						// The avatar is rendered at a fixed 64px, and is always square
						width={64}
						height={64}
						responsiveSizesSettings={'64px'}
						// Decorative: the author name is always rendered next to it,
						// so a description here would only be announced twice
						altText={''}
						lazyLoading={lazyLoading}
					/>
				</div>
				<div className={styles.details}>
					<p className={styles.name} id={nameId}>
						{name}
					</p>
					{jobTitle && <p className={styles.jobTitle}>{jobTitle}</p>}
				</div>
			</div>
			{biography && <p className={styles.biography}>{biography}</p>}
			{hasSocialLinks && (
				<div className={styles.socialLinks}>
					{linkedInUrl && (
						<Link
							className={styles.socialLink}
							linkUrl={linkedInUrl}
							rel={'noopener noreferrer'}
							aria-label={linkedInAriaLabel || undefined}
							color={'active'}
							size={'sm'}
							weight={'bold'}
							hideExternalIcon
						>
							<Icon className={styles.socialIcon} name={'linkedin'} size={'sm'} />
							{linkedInLabel}
						</Link>
					)}
					{emailAddress && (
						<Link
							className={styles.socialLink}
							mailTo={emailAddress}
							target={'_self'}
							aria-label={contactAriaLabel || undefined}
							color={'active'}
							size={'sm'}
							weight={'bold'}
							hideExternalIcon
						>
							<Icon className={styles.socialIcon} name={'mail'} size={'sm'} />
							{contactLabel}
						</Link>
					)}
				</div>
			)}
		</aside>
	);
};
